import { create } from 'zustand'
import { Button } from '@floorfile/ui'
import { useCopy } from '@floorfile/i18n'
import { useEffect } from 'react'
import { cn } from '@/lib/utils'
import { apiErrorMessage, errorCopy } from '@/lib/errors'

/**
 * Minimal global surface for "that didn't save" (audit RUN-9).
 *
 * THE PROBLEM: 85 call sites fire `void mutation.mutateAsync(...)`. `void`
 * satisfies the no-floating-promises lint rule while attaching NO handler, and
 * `mutateAsync` rejects on failure (unlike `mutate`). The QueryClient had no
 * `mutationCache`, so there was no central safety net either — meaning every
 * failed mutation was an unhandled rejection with ZERO user feedback: the
 * control silently did not persist and the user believed it had. Several of
 * those sites are destructive (remove, archive, revoke, delete).
 *
 * This is deliberately small — a floor, not a design system. Per-mutation
 * `onError` handling remains the better treatment where a page can say
 * something more specific; this guarantees the user is never left believing a
 * failed action succeeded.
 */

export interface ErrorToast {
  id: number
  message: string
  /** FF-AUDIT-20260914-210459-099: the same floor carries a short success
   * note ("NCR-2026-00010 raised") — `status`, not `alert`, and gone sooner. */
  tone: 'error' | 'success'
  /** FF-QA-144: the API's request id, so a person can give support the line to find. */
  reference?: string | undefined
}

interface ToastState {
  toasts: ErrorToast[]
  push: (message: string, tone?: 'error' | 'success', reference?: string) => void
  dismiss: (id: number) => void
}

/** The envelope's `requestId`, whichever transport threw (`ApiError` or the offline path's plain Error). */
export function errorReference(error: unknown): string | undefined {
  const id = (error as { requestId?: unknown } | null)?.requestId
  return typeof id === 'string' && id ? id : undefined
}

let nextId = 1

export const useErrorToasts = create<ToastState>()((set) => ({
  toasts: [],
  push: (message, tone = 'error', reference) =>
    set((s) => {
      // Collapse repeats: a failing bulk action should not stack ten identical
      // toasts over the screen a technician is trying to work in.
      if (s.toasts.some((t) => t.message === message)) return s
      return { toasts: [...s.toasts, { id: nextId++, message, tone, reference }].slice(-3) }
    }),
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}))

/**
 * Report a failed mutation to the user. Safe to call outside React.
 *
 * Goes through the error catalogue rather than rendering `error.message`
 * directly. The server's `message` is a developer string — it is what Sentry
 * and `audit_log` read — and for the platform codes the catalogue says the same
 * thing in the user's language and in words that name the next action.
 */
export function reportMutationError(error: unknown): void {
  useErrorToasts.getState().push(apiErrorMessage(error), 'error', errorReference(error))
}

/** FF-AUDIT-099: a create or send that worked says so, briefly. Safe outside React. */
export function reportSuccess(message: string): void {
  useErrorToasts.getState().push(message, 'success')
}

function Toast({ toast }: { toast: ErrorToast }) {
  const c = useCopy(errorCopy)
  const dismiss = useErrorToasts((s) => s.dismiss)
  useEffect(() => {
    const timer = window.setTimeout(() => dismiss(toast.id), toast.tone === 'success' ? 4_000 : 8_000)
    return () => window.clearTimeout(timer)
  }, [dismiss, toast.id, toast.tone])
  return (
    <div
      // `alert` (not `status`) so assistive tech announces an ERROR immediately —
      // the user is about to act on the belief that their change saved. A
      // success note is polite (`status`).
      role={toast.tone === 'success' ? 'status' : 'alert'}
      className={cn(
        'pointer-events-auto flex items-start gap-3 rounded-md border',
        toast.tone === 'success' ? 'border-success/40' : 'border-destructive/40',
        'bg-card p-3 text-sm shadow-md',
      )}
    >
      <span className="flex-1 text-foreground">
        {toast.message}
        {toast.reference && <span className="mt-1 block select-all font-mono text-xs text-muted-foreground">{c.reference({ id: toast.reference })}</span>}
      </span>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => dismiss(toast.id)}
        aria-label={toast.tone === 'success' ? c.dismissNote : c.dismiss}
        className="shrink-0 px-2 text-muted-foreground hover:text-foreground"
      >
        ✕
      </Button>
    </div>
  )
}

export function ErrorToasts() {
  const toasts = useErrorToasts((s) => s.toasts)
  if (toasts.length === 0) return null
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-50 mx-auto flex w-full max-w-md flex-col gap-2 px-4">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} />
      ))}
    </div>
  )
}
